import {
  GetCreateSignalOrTradeGeneralDataResponse,
  OrderTypes,
  QueryStatus,
  LanguageNameSpaces,
} from "@shared/types";
import {
  AmountData,
  TradeFormValues,
  TradeTypes,
} from "@shared/features/tradingTerminal/common/types";
import {
  ErrorTypeToDisplay,
  ErrorTypes,
} from "@shared/features/trades/common/types";
import {
  getOrdersWithPrecision,
  validateGeneralTradeData,
  validateOrders,
} from "@shared/features/tradingTerminal/common/utils";
import { getErrorDisplayMessage } from "@shared/features/trades/common/utils";
import {
  DEFAULT_SIGNAL_AMOUNT,
  PERCENT_PRECISION,
} from "@shared/features/tradingTerminal/common/constants";
import { round } from "lodash";
import { useMemo } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useErrorsProps {
  formValues: TradeFormValues;
  amountData: AmountData;
  getCreateGeneralDataQuery: QueryStatus<GetCreateSignalOrTradeGeneralDataResponse>;
  isSignal?: boolean;
  currentPrice?: number;
}

const useErrors = ({
  formValues,
  amountData,
  getCreateGeneralDataQuery,
  isSignal,
  currentPrice,
}: useErrorsProps) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const { t: tTrades } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const {
    entryOrders,
    takeProfitOrders,
    stopOrders,
    useTakeProfitOrders,
    useStopOrders,
    extraInfo,
    amount,
    amountType,
    symbols,
    account,
    quantity,
    exchanges,
    subGroupIds,
  } = formValues;

  const {
    availableQuantity,
    availableAmount,
    minAmount,
    priceLimits,
    getMinAmountByPrice,
    minQuantity,
    precisionConfig,
  } = amountData;

  const createTradeGeneralData = getCreateGeneralDataQuery?.data;
  const ordersToValidate = useMemo(() => {
    return {
      [OrderTypes.ENTRY]: getOrdersWithPrecision(
        entryOrders,
        PERCENT_PRECISION
      ),
      [OrderTypes.TAKE_PROFIT]: useTakeProfitOrders
        ? getOrdersWithPrecision(takeProfitOrders, PERCENT_PRECISION)
        : [],
      [OrderTypes.STOP]: useStopOrders ? stopOrders : [],
    };
  }, [
    entryOrders,
    takeProfitOrders,
    stopOrders,
    useTakeProfitOrders,
    useStopOrders,
  ]);

  const sharedDeps = [
    availableAmount,
    minAmount,
    quantity,
    minQuantity,
    ordersToValidate,
    extraInfo,
    currentPrice,
  ];

  const ordersErrors = useMemo(() => {
    return validateOrders(
      ordersToValidate,
      !!extraInfo?.reverse,
      extraInfo?.trade_type === TradeTypes.BREAKOUT,
      currentPrice || 0,
      availableAmount,
      minAmount || 0,
      priceLimits.min,
      priceLimits.max,
      getMinAmountByPrice,
      minQuantity,
      Number(quantity),
      extraInfo?.cancel_on_first_target_reached,
      isSignal
    );
  }, [
    ...sharedDeps,
    priceLimits.min,
    priceLimits.max,
    getMinAmountByPrice,
    isSignal,
  ]);

  const generalErrorsMapping = useMemo(() => {
    const rawAmount =
      isSignal && !amountType ? DEFAULT_SIGNAL_AMOUNT : amount || "0";
    return validateGeneralTradeData(
      t,
      round(Number(rawAmount), precisionConfig?.amount),
      symbols[0],
      availableAmount,
      minAmount || 0,
      quantity,
      availableQuantity,
      minQuantity,
      ordersToValidate,
      account?.client_name,
      extraInfo,
      createTradeGeneralData,
      currentPrice,
      isSignal,
      exchanges,
      subGroupIds
    );
  }, [
    ...sharedDeps,
    amount,
    amountType,
    precisionConfig?.amount,
    symbols[0],
    availableQuantity,
    account?.client_name,
    createTradeGeneralData,
    exchanges,
    subGroupIds,
    i18n.language,
  ]);

  const generalErrors = useMemo(
    () => Object.keys(generalErrorsMapping),
    [generalErrorsMapping]
  ) as unknown as ErrorTypes[];

  const existingQuantityErrors: ErrorTypeToDisplay = useMemo(
    () =>
      generalErrors.reduce((acc: ErrorTypeToDisplay, error) => {
        if (EXISTING_QUANTITY_ERRORS.includes(error)) {
          acc[error as unknown as ErrorTypes] = undefined;
        }
        return acc;
      }, {}),
    [generalErrors]
  );

  const ordersErrorsValues = useMemo(
    () => Object.values(ordersErrors),
    [ordersErrors]
  );
  let error: ErrorTypes | undefined = undefined;
  const numErrors = useMemo(
    () =>
      ordersErrorsValues.reduce((acc, orderSectionErrors) => {
        const sectionErrors = Object.keys(
          orderSectionErrors.section
        ) as unknown as ErrorTypes[];
        const numSectionErrors = sectionErrors.length;
        if (error === undefined) error = sectionErrors?.[0];
        acc += numSectionErrors;
        return acc;
      }, generalErrors?.length),
    [ordersErrorsValues, error, generalErrors?.length]
  );
  if (error === undefined) error = generalErrors?.[0];

  const numErrorsMessage = useMemo(() => {
    let text = t("modals.trade_ready");
    if (numErrors === 1) {
      text =
        generalErrorsMapping[error!] ??
        (getErrorDisplayMessage(tTrades, error!) ||
          t("messages.errors.unresolved_issue"));
    } else if (numErrors > 1) {
      text = `You have ${numErrors} unresolved issues left`;
    }
    return text;
  }, [numErrors, generalErrorsMapping[error], error, t]);

  return {
    generalErrors: generalErrorsMapping,
    ordersErrors,
    existingQuantityErrors,
    numErrors,
    numErrorsMessage,
  };
};

export default useErrors;

const EXISTING_QUANTITY_ERRORS: ErrorTypes[] = [
  ErrorTypes.INVALID_TRADE_QUANTITY,
  ErrorTypes.INVALID_NUMBER_OF_TAKE_PROFIT_AND_STOP,
];
